import { createRouter, createWebHashHistory } from 'vue-router';
import { useUserStore } from '@/stores/user';

// 移动设备 UA 检测：用于供应商管理员登录后路由分流（手机走 H5、PC 走后台）
function isMobileUA() {
  return /Mobile|Android|iPhone|iPad|iPod|MicroMessenger/i.test(navigator.userAgent || '');
}

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/Login.vue'),
    meta: { guest: true },
  },
  {
    path: '/register',
    name: 'Register',
    component: () => import('@/views/Register.vue'),
    meta: { guest: true },
  },
  {
    path: '/h5/register',
    name: 'H5Register',
    component: () => import('@/views/h5/Register.vue'),
    meta: { guest: true },
  },
  {
    path: '/wechat-pc-callback',
    name: 'WechatPcCallback',
    component: () => import('@/views/WechatPcCallback.vue'),
    meta: { guest: true },
  },
  {
    path: '/wechat-pc-scan',
    name: 'WechatPcScan',
    component: () => import('@/views/WechatPcScan.vue'),
    meta: { guest: true },
  },
  {
    path: '/wechat-bind-pc',
    name: 'WechatBindPc',
    component: () => import('@/views/WechatBindPc.vue'),
    meta: { guest: true },
  },
  // ===== PC管理端（超管 + 单位管理员） =====
  {
    path: '/admin',
    component: () => import('@/layouts/AdminLayout.vue'),
    meta: { requireAuth: true, roles: ['super_admin', 'customer_admin'] },
    children: [
      { path: '', redirect: '/admin/dashboard' },
      { path: 'dashboard', name: 'Dashboard', component: () => import('@/views/admin/Dashboard.vue'), meta: { title: '首页' } },
      { path: 'customers', name: 'Customers', component: () => import('@/views/admin/Customers.vue'), meta: { title: '单位管理', roles: ['super_admin'] } },
      { path: 'departments', name: 'Departments', component: () => import('@/views/admin/Departments.vue'), meta: { title: '科室管理' } },
      { path: 'users', name: 'Users', component: () => import('@/views/admin/Users.vue'), meta: { title: '用户管理' } },
      { path: 'suppliers', name: 'Suppliers', component: () => import('@/views/admin/Suppliers.vue'), meta: { title: '供应商管理', roles: ['super_admin'] } },
      { path: 'supplier-quotas', name: 'SupplierQuotas', component: () => import('@/views/admin/SupplierQuotas.vue'), meta: { title: '供应商额度', roles: ['customer_admin', 'super_admin'] } },
      { path: 'supplier-applications', name: 'AdminSupplierApplications', component: () => import('@/views/admin/SupplierApplications.vue'), meta: { title: '供应商入驻审核' } },
      { path: 'categories', name: 'Categories', component: () => import('@/views/admin/Categories.vue'), meta: { title: '经营品类字典', roles: ['super_admin'] } },
      { path: 'products', name: 'Products', component: () => import('@/views/admin/Products.vue'), meta: { title: '商品管理', roles: ['super_admin'] } },
      { path: 'product-audit', name: 'ProductAudit', component: () => import('@/views/admin/ProductAudit.vue'), meta: { title: '商品审核', roles: ['customer_admin'] } },
      { path: 'orders', name: 'Orders', component: () => import('@/views/admin/Orders.vue'), meta: { title: '订单管理' } },
      { path: 'order-audit', name: 'OrderAudit', component: () => import('@/views/admin/OrderAudit.vue'), meta: { title: '订单审核', roles: ['customer_admin'] } },
      { path: 'signatures', name: 'Signatures', component: () => import('@/views/admin/Signatures.vue'), meta: { title: '签收记录' } },
      { path: 'stats', name: 'Stats', component: () => import('@/views/admin/Stats.vue'), meta: { title: '数据统计' } },
      { path: 'invoices', name: 'Invoices', component: () => import('@/views/admin/Invoices.vue'), meta: { title: '发票管理' } },
      { path: 'api-clients', name: 'ApiClients', component: () => import('@/views/admin/ApiClients.vue'), meta: { title: '开放接口', roles: ['super_admin'] } },
      { path: 'settings', name: 'Settings', component: () => import('@/views/admin/Settings.vue'), meta: { title: '系统设置' } },
    ],
  },
  // ===== PC供应商后台 =====
  {
    path: '/supplier',
    component: () => import('@/layouts/SupplierLayout.vue'),
    meta: { requireAuth: true, roles: ['supplier_admin'] },
    children: [
      { path: '', redirect: '/supplier/dashboard' },
      { path: 'dashboard', name: 'SupplierDashboard', component: () => import('@/views/supplier/Dashboard.vue'), meta: { title: '首页' } },
      { path: 'products', name: 'SupplierProducts', component: () => import('@/views/supplier/Products.vue'), meta: { title: '商品管理' } },
      { path: 'orders', name: 'SupplierOrders', component: () => import('@/views/supplier/Orders.vue'), meta: { title: '订单管理' } },
      { path: 'delivery-print/:id', name: 'DeliveryPrint', component: () => import('@/views/supplier/DeliveryPrint.vue'), meta: { title: '送货单打印', roles: ['supplier_admin'] } },
      { path: 'warehouse-print', name: 'WarehousePrint', component: () => import('@/views/supplier/WarehousePrint.vue'), meta: { title: '入库单打印', roles: ['supplier_admin'] } },
      { path: 'deliveries', name: 'SupplierDeliveries', component: () => import('@/views/supplier/Deliveries.vue'), meta: { title: '配送员管理' } },
      { path: 'applications', name: 'SupplierApplications', component: () => import('@/views/supplier/Applications.vue'), meta: { title: '入驻管理' } },
      { path: 'stats', name: 'SupplierStats', component: () => import('@/views/supplier/Stats.vue'), meta: { title: '对账统计' } },
      { path: 'invoices', name: 'SupplierInvoices', component: () => import('@/views/supplier/Invoices.vue'), meta: { title: '发票管理' } },
    ],
  },
  // ===== PC配送员工作台 =====
  {
    path: '/delivery',
    component: () => import('@/layouts/DeliveryLayout.vue'),
    meta: { requireAuth: true, roles: ['delivery'] },
    children: [
      { path: '', redirect: '/delivery/orders' },
      { path: 'orders', name: 'DeliveryOrders', component: () => import('@/views/delivery/Orders.vue'), meta: { title: '我的配送' } },
    ],
  },
  // ===== PC科室端 =====
  {
    path: '/dept',
    component: () => import('@/layouts/DeptLayout.vue'),
    meta: { requireAuth: true, roles: ['dept_user'] },
    children: [
      { path: '', redirect: '/dept/home' },
      { path: 'home', name: 'DeptHome', component: () => import('@/views/dept/Home.vue'), meta: { title: '商品选购' } },
      { path: 'orders', name: 'DeptOrders', component: () => import('@/views/dept/Orders.vue'), meta: { title: '我的订单' } },
      { path: 'order/:id', name: 'DeptOrderDetail', component: () => import('@/views/dept/OrderDetail.vue'), meta: { title: '订单详情' } },
      { path: 'sign/:id', name: 'DeptSign', component: () => import('@/views/dept/Sign.vue'), meta: { title: '电子签收' } },
    ],
  },
  // ===== 手机H5端 =====
  {
    path: '/h5',
    component: () => import('@/layouts/H5Layout.vue'),
    meta: { requireAuth: true },
    children: [
      { path: '', redirect: '/h5/home' },
      { path: 'home', name: 'H5Home', component: () => import('@/views/h5/Home.vue'), meta: { title: '首页', showTabbar: true } },
      { path: 'products', name: 'H5Products', component: () => import('@/views/h5/Products.vue'), meta: { title: '商品列表', showTabbar: true } },
      { path: 'cart', name: 'H5Cart', component: () => import('@/views/h5/Cart.vue'), meta: { title: '购物车', showTabbar: true } },
      { path: 'orders', name: 'H5Orders', component: () => import('@/views/h5/Orders.vue'), meta: { title: '我的订单', showTabbar: true } },
      { path: 'order/:id', name: 'H5OrderDetail', component: () => import('@/views/h5/OrderDetail.vue'), meta: { title: '订单详情' } },
      { path: 'sign/:id', name: 'H5Sign', component: () => import('@/views/h5/Sign.vue'), meta: { title: '电子签收' } },
      // ===== 单位管理员 H5 =====
      { path: 'admin-home', name: 'H5AdminHome', component: () => import('@/views/h5/AdminHome.vue'), meta: { title: '工作台', showTabbar: true, roles: ['customer_admin'] } },
      { path: 'audit-orders', name: 'H5AuditOrders', component: () => import('@/views/h5/AuditOrders.vue'), meta: { title: '订单审核', showTabbar: true, roles: ['customer_admin'] } },
      { path: 'audit-products', name: 'H5AuditProducts', component: () => import('@/views/h5/AuditProducts.vue'), meta: { title: '商品审核', showTabbar: true, roles: ['customer_admin'] } },
      // ===== 供应商管理员 H5 =====
      { path: 'supplier-home', name: 'H5SupplierHome', component: () => import('@/views/h5/SupplierHome.vue'), meta: { title: '供应商工作台', showTabbar: true, roles: ['supplier_admin'] } },
      { path: 'supplier-orders', name: 'H5SupplierOrders', component: () => import('@/views/h5/SupplierOrders.vue'), meta: { title: '订单管理', showTabbar: true, roles: ['supplier_admin'] } },
      { path: 'supplier-order/:id', name: 'H5SupplierOrderDetail', component: () => import('@/views/h5/SupplierOrderDetail.vue'), meta: { title: '订单详情', roles: ['supplier_admin'] } },
      { path: 'profile', name: 'H5Profile', component: () => import('@/views/h5/Profile.vue'), meta: { title: '个人中心', showTabbar: true } },
      { path: 'wechat-callback', name: 'H5WechatCallback', component: () => import('@/views/h5/WechatCallback.vue'), meta: { title: '微信登录', guest: true } },
    ],
  },
  // 默认跳转
  { path: '/', redirect: '/login' },
  { path: '/:pathMatch(.*)*', name: 'NotFound', component: () => import('@/views/Login.vue'), meta: { guest: true } },
];

const router = createRouter({
  history: createWebHashHistory(),
  routes,
});

// 路由守卫
router.beforeEach((to, from, next) => {
  const userStore = useUserStore();

  if (to.meta.guest) {
    if (userStore.isLoggedIn) {
      // 已登录则根据角色跳转
      if (userStore.isAdmin) {
        return next('/admin/dashboard');
      }
      if (userStore.isCustomerAdmin) {
        return next(isMobileUA() ? '/h5/admin-home' : '/admin/dashboard');
      }
      if (userStore.isSupplierAdmin) {
        return next(isMobileUA() ? '/h5/supplier-home' : '/supplier/dashboard');
      }
      if (userStore.isDeptUser) {
        return next(isMobileUA() ? '/h5/home' : '/dept/home');
      }
      if (userStore.isDelivery) {
        return next(isMobileUA() ? '/h5/home' : '/delivery/orders');
      }
      return next('/h5/home');
    }
    return next();
  }

  if (to.meta.requireAuth && !userStore.isLoggedIn) {
    return next('/login');
  }

  // 角色检查
  const roles = to.meta.roles;
  if (roles && !roles.includes(userStore.user?.role)) {
    return next('/login');
  }

  // 404 页面：已登录用户按角色跳转首页，未登录去登录
  if (to.name === 'NotFound') {
    if (userStore.isLoggedIn) {
      if (userStore.isAdmin) return next('/admin/dashboard');
      if (userStore.isCustomerAdmin) return next(isMobileUA() ? '/h5/admin-home' : '/admin/dashboard');
      if (userStore.isSupplierAdmin) return next(isMobileUA() ? '/h5/supplier-home' : '/supplier/dashboard');
      if (userStore.isDeptUser) return next(isMobileUA() ? '/h5/home' : '/dept/home');
      if (userStore.isDelivery) return next(isMobileUA() ? '/h5/home' : '/delivery/orders');
      return next('/h5/home');
    }
    return next('/login');
  }

  next();
});

export default router;
